import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { TextField, Button, MenuItem } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import ruLocale from 'date-fns/locale/ru';
import enLocale from 'date-fns/locale/en-US';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as locationActions } from '../../../../store/ducks/yaLocations.duck';
import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import AutocompleteLocations from '../../../../components/AutocompleteLocations';
import { useStyles } from '../hooks/useStyles';
import { IAppState } from '../../../../store/rootDuck';
import { ILocation } from '../../../../interfaces/locations';
import { IOrderType } from '../../../../interfaces/order';
import { getStatusTypeWithTranslates } from '../constants';
import { IStore } from '../../../../interfaces/store';
import { DeliveryType } from '../../cart/hooks/useFormikCart';
import { CdekCities } from '../../cart/components/CdekCities';
import { AutocompleteSelf } from '../../cart/components/AutocompleteSelf';

const locales: { [key: string]: any } = {
  ru: ruLocale,
  en: enLocale,
};

export const OrderForm = ({
  formik,
  disabled,
  submitCallback,
  chosenCdekCity,
  setChosenCdekCity,
  shippingLocation,
  orderTypes,
  stores,
}: {
  formik: any;
  disabled: boolean;
  submitCallback: () => void;
  chosenCdekCity: any;
  setChosenCdekCity: (city: any) => void;
  shippingLocation: ILocation | null;
  setShippingLocation: (loc: ILocation | null) => void;
  cdekCitiesToShow: any;
  orderTypes: IOrderType[];
  stores: IStore[];
}) => {
  const intl = useIntl();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const translatesStatus: { [type: string]: string } = {
    paid: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    not_paid: intl.formatMessage({ id: 'ORDER.TYPE.NOT_PAID' }),
  };

  const orderStatusTypes = getStatusTypeWithTranslates(translatesStatus);

  const { order, editLoading } = useSelector(
    ({ orders }: IAppState) => ({
      order: orders.order,
      editLoading: orders.editLoading,
    }),
    shallowEqual
  );

  const { loadingLocations, locations, byCoordsLoading } = useSelector(
    ({ yaLocations }: IAppState) => ({
      loadingLocations: yaLocations.loading,
      locations: yaLocations.yaLocations,
      byCoordsLoading: yaLocations.getLoacationByCoordsLoading,
    }),
    shallowEqual
  );

  const { values, handleBlur, handleChange, errors, touched, setFieldValue } = formik;

  return (
    <>
      <TextField
        type='text'
        label={intl.formatMessage({ id: 'ORDER.INPUT.NAME' })}
        margin='normal'
        name='fio'
        value={values.fio}
        variant='outlined'
        helperText={touched.fio && errors.fio}
        error={Boolean(touched.fio && errors.fio)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        type='text'
        label={intl.formatMessage({ id: 'ORDER.INPUT.PHONE' })}
        margin='normal'
        name='phone'
        value={values.phone}
        variant='outlined'
        helperText={touched.phone && errors.phone}
        error={Boolean(touched.phone && errors.phone)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        type='email'
        label={intl.formatMessage({ id: 'USER.EDIT.EMAIL' })}
        margin='normal'
        name='email'
        value={values.email}
        variant='outlined'
        helperText={touched.email && errors.email}
        error={Boolean(touched.email && errors.email)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        select
        margin='normal'
        label='Вид доставки'
        value={values.deliveryType || 'delivery'}
        onChange={handleChange}
        name='deliveryType'
        variant='outlined'
        helperText={touched.deliveryType && errors.deliveryType}
        error={Boolean(touched.deliveryType && errors.deliveryType)}
        disabled
      >
        <MenuItem value={DeliveryType.self_store}>Самовывоз от продавца</MenuItem>
        <MenuItem value={DeliveryType.delivery}>Доставка СДЭК до адреса</MenuItem>
        <MenuItem value={DeliveryType.self}>Доставка до пункта СДЭК</MenuItem>
      </TextField>

      <MuiPickersUtilsProvider locale={locales[intl.locale]} utils={DateFnsUtils}>
        <KeyboardDatePicker
          label={intl.formatMessage({ id: 'ORDER.DELIVERY.DATE' })}
          margin='normal'
          name='received_at'
          format='dd MMMM'
          value={values.receivedAtString}
          helperText={touched.receivedAtString && errors.receivedAtString}
          error={Boolean(touched.receivedAtString && errors.receivedAtString)}
          onBlur={handleBlur}
          onChange={date => {
            setFieldValue('receivedAtString', date);
          }}
          minDate={Date.now()}
          inputVariant='outlined'
          disabled={disabled}
        />
      </MuiPickersUtilsProvider>

      {values.deliveryType === DeliveryType.self_store && (
        <TextField
          select
          type='country'
          label='Склад доставки'
          margin='normal'
          className='kt-width-full'
          variant='outlined'
          value={values?.store?.id || undefined}
          onChange={e => {}}
          disabled
          error={Boolean(touched.store && errors.store && errors.store.id)}
          helperText={touched.store && errors.store && errors.store.id}
        >
          {stores.map(item => (
            <MenuItem key={item.id} value={item.id || 0}>
              {item?.location?.address || ''}
            </MenuItem>
          ))}
        </TextField>
      )}

      {values.deliveryType === DeliveryType.delivery && (
        <>
          <CdekCities
            chosenCdekCity={values.city}
            fullChosenCdekCity={values.cityFull}
            readOnly
            setChosenCdekCity={(city, fullName) => {}}
          />
          <AutocompleteLocations
            options={locations || []}
            loading={loadingLocations || byCoordsLoading}
            inputValue={values.locationDelivery}
            editable
            label={intl.formatMessage({
              id: 'STORE.INPUT.LOCATION',
            })}
            inputClassName={{ width: '100%' }}
            inputError={Boolean(
              touched.locationDelivery &&
                errors.locationDelivery &&
                errors.locationDelivery.address
            )}
            inputHelperText={
              touched.locationDelivery &&
              errors.locationDelivery &&
              errors.locationDelivery.address
            }
            fetchLocations={data =>
              dispatch(
                locationActions.fetchRequest({
                  ...data,
                  location: `${values.city ? `${values.city}, ` : ''} ${data.location}`,
                })
              )
            }
            clearLocations={() => dispatch(locationActions.clear())}
            setSelectedLocation={location => {}}
            handleBlur={handleBlur}
            disable
          />
        </>
      )}

      {values.deliveryType === DeliveryType.self && (
        <AutocompleteSelf
          formik={formik}
          setPickupLocation={pickupLocation => {}}
          shippingLocation={shippingLocation}
          chosenCdekCity={chosenCdekCity ? { label: chosenCdekCity } : undefined}
          setChosenCdekCity={setChosenCdekCity}
          readOnly
          order={order}
        />
      )}
      <TextField
        select
        margin='normal'
        label={`${intl.formatMessage({ id: 'ORDER.INPUT.STATUS' })}`}
        value={values.status || ''}
        onChange={handleChange}
        name='status'
        variant='outlined'
        helperText={touched.status && errors.status}
        error={Boolean(touched.status && errors.status)}
        disabled={disabled}
      >
        {orderTypes.map(item => (
          <MenuItem value={item.type}>{item.translate}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        margin='normal'
        label={`${intl.formatMessage({ id: 'ORDER.TABLE.STATUS.PAYMENT' })}`}
        value={values.payment_status || ''}
        onChange={handleChange}
        name='payment_status'
        variant='outlined'
        helperText={touched.payment_status && errors.payment_status}
        error={Boolean(touched.payment_status && errors.payment_status)}
        disabled={disabled}
      >
        {orderStatusTypes.map(item => (
          <MenuItem value={item.type}>{item.translate}</MenuItem>
        ))}
      </TextField>
      {!disabled && (
        <div className={classes.bottomActions}>
          <Button
            onClick={() => history.goBack()}
            className={classes.buttons}
            variant='outlined'
            color='primary'
          >
            {intl.formatMessage({ id: 'CATEGORY.BUTTON.CANCEL' })}
          </Button>
          <ButtonWithLoader
            disabled={editLoading}
            loading={editLoading}
            onPress={submitCallback}
          >
            {intl.formatMessage({ id: 'COMMON.BUTTON.SAVE' })}
          </ButtonWithLoader>
        </div>
      )}
    </>
  );
};
